import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import createCachedSelector from 're-reselect';
import { RootState } from 'store';
import { handleOrgSwitch, logout, softLogout } from 'store/actions';
import { fetchPartData } from 'store/slices/apiV1/partData';
import { Part, PartId, RetryDirective } from 'types/part';
import {
  DefaultPartRequestState,
  LoadingStatusTypes,
  PartRequestStateType,
  PartSchemas,
  partStatusRequestTypes,
} from 'types/partStatus';

export type PartStatusStateProps = {
  [key: PartId]: PartRequestStateType;
};

export const initialStatePartStatus: {
  [key: PartId]: PartRequestStateType;
} = {};

const partStatus = createSlice({
  name: 'partStatus',
  initialState: initialStatePartStatus,
  reducers: {
    createOrUpdatePartsStatus(state, action) {
      const { partIds, schemas, statusType } = action.payload;
      _.forEach(partIds, (partId: Part['id']) => {
        if (!state[partId]) {
          state[partId] = structuredClone(DefaultPartRequestState);
        }
        _.forEach(schemas, (schema) => {
          state[partId][schema as PartSchemas] = statusType;
        });
      });
    },
    removePartStatusByIdAndSchema(state, action) {
      const { partId, schema } = action.payload;
      state[partId][schema as PartSchemas] = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialStatePartStatus)
      .addCase(softLogout, () => initialStatePartStatus)
      .addCase(handleOrgSwitch, (state, action) => {
        const { customPartIds } = action.payload;
        //   remove part status for each supply and alt in the old org
        _.forEach(Object.keys(state), (partId) => {
          state[partId][PartSchemas.supply] = null;
          state[partId][PartSchemas.alts] = null;
        });
        _.forEach(customPartIds, (partId) => {
          state[partId][PartSchemas.core] = null;
          state[partId][PartSchemas.specs] = null;
        });
      })
      .addCase(fetchPartData.pending, (state, { meta }) => {
        const schemas: PartSchemas[] = meta.arg.schemas;
        const partIds = meta.arg.parts;
        for (const partId of partIds) {
          if (!state[partId]) {
            state[partId] = structuredClone(DefaultPartRequestState);
          }
          if (schemas.includes(PartSchemas.core)) {
            state[partId][PartSchemas.core] = partStatusRequestTypes.FETCHING;
          }
          if (schemas.includes(PartSchemas.supply)) {
            let supplyStatus = partStatusRequestTypes.FETCHING;
            if (
              state[partId][PartSchemas.supply] ===
              partStatusRequestTypes.READY_RETRY_NOW
            ) {
              supplyStatus = partStatusRequestTypes.FETCHING_RETRY;
            }
            state[partId][PartSchemas.supply] = supplyStatus;
          }
          if (schemas.includes(PartSchemas.alts)) {
            state[partId][PartSchemas.alts] = partStatusRequestTypes.FETCHING;
          }
          if (schemas.includes(PartSchemas.specs)) {
            state[partId][PartSchemas.specs] = partStatusRequestTypes.FETCHING;
          }
          if (schemas.includes(PartSchemas.market)) {
            state[partId][PartSchemas.market] = partStatusRequestTypes.FETCHING;
          }
          if (schemas.includes(PartSchemas.suggestions)) {
            state[partId][PartSchemas.suggestions] =
              partStatusRequestTypes.FETCHING;
          }
        }
      })
      .addCase(fetchPartData.fulfilled, (state, action) => {
        const schemas: PartSchemas[] = action.meta.arg.schemas;
        const requestedPartIds = action.meta.arg.parts;
        const returnedPartsById = _.keyBy(
          action.payload as { id: PartId; retryDirective?: RetryDirective }[],
          'id'
        );
        for (const partId of requestedPartIds) {
          if (!state[partId]) {
            state[partId] = structuredClone(DefaultPartRequestState);
          }
          const part = returnedPartsById[partId];
          if (!part) {
            _.forEach(schemas, (schema) => {
              const status = state[partId]?.[schema];
              if (status && LoadingStatusTypes.includes(status)) {
                state[partId][schema as PartSchemas] =
                  partStatusRequestTypes.ERROR;
              }
            });
          } else {
            if (schemas.includes(PartSchemas.core)) {
              state[part.id][PartSchemas.core] = partStatusRequestTypes.SUCCESS;
            }
            if (schemas.includes(PartSchemas.supply)) {
              let supplyStatus = partStatusRequestTypes.SUCCESS;
              if (part.retryDirective === RetryDirective.retry_now) {
                if (
                  state[part.id][PartSchemas.supply] !==
                  partStatusRequestTypes.FETCHING_RETRY
                ) {
                  supplyStatus = partStatusRequestTypes.READY_RETRY_NOW;
                } else {
                  supplyStatus = partStatusRequestTypes.RETRY_NOW_FAILED;
                }
              }
              if (part.retryDirective === RetryDirective.no_retry) {
                supplyStatus = partStatusRequestTypes.KB_FAILURE;
              }
              state[part.id][PartSchemas.supply] = supplyStatus;
            }
            if (schemas.includes(PartSchemas.alts)) {
              state[part.id][PartSchemas.alts] = partStatusRequestTypes.SUCCESS;
            }
            if (schemas.includes(PartSchemas.specs)) {
              state[part.id][PartSchemas.specs] =
                partStatusRequestTypes.SUCCESS;
            }
            if (schemas.includes(PartSchemas.market)) {
              state[part.id][PartSchemas.market] =
                partStatusRequestTypes.SUCCESS;
            }
            if (schemas.includes(PartSchemas.suggestions)) {
              state[part.id][PartSchemas.suggestions] =
                partStatusRequestTypes.SUCCESS;
            }
          }
        }
      })
      .addCase(fetchPartData.rejected, (state, action) => {
        const schemas: PartSchemas[] = action.meta.arg.schemas;
        const partIds = action.meta.arg.parts;
        for (const partId of partIds) {
          if (!state[partId]) {
            state[partId] = structuredClone(DefaultPartRequestState);
          }
          if (schemas.includes(PartSchemas.core)) {
            state[partId][PartSchemas.core] = partStatusRequestTypes.ERROR;
          }
          if (schemas.includes(PartSchemas.supply)) {
            state[partId][PartSchemas.supply] = partStatusRequestTypes.ERROR;
          }
          if (schemas.includes(PartSchemas.alts)) {
            state[partId][PartSchemas.alts] = partStatusRequestTypes.ERROR;
          }
          if (schemas.includes(PartSchemas.specs)) {
            state[partId][PartSchemas.specs] = partStatusRequestTypes.ERROR;
          }
          if (schemas.includes(PartSchemas.market)) {
            state[partId][PartSchemas.market] = partStatusRequestTypes.ERROR;
          }
          if (schemas.includes(PartSchemas.suggestions)) {
            state[partId][PartSchemas.suggestions] =
              partStatusRequestTypes.ERROR;
          }
        }
      });
  },
});

export default partStatus.reducer;

export const { createOrUpdatePartsStatus, removePartStatusByIdAndSchema } =
  partStatus.actions;

export const getPartStatusSelector = (state: RootState) => state.partStatus;

createCachedSelector(
  getPartStatusSelector,
  (__: RootState, schemas: PartSchemas[]) => schemas,
  (__: RootState, ___, partId: PartId) => partId,
  (partStatusState, schemas, partId) => {
    let loading = false;
    _.forEach(schemas, (s) => {
      const status = partStatusState[partId]?.[s];
      if (status && LoadingStatusTypes.includes(status)) {
        loading = true;
      }
    });
    return loading;
  }
)((__, schemas, partId) => `${partId}_${(_.join(schemas), '_')}`);

export const selectLoadingStateByStatusByIdAndSchemaList = createCachedSelector(
  getPartStatusSelector,
  (__: RootState, schemas: PartSchemas[]) => schemas,
  (__: RootState, ___, partIds: PartId[]) => partIds,
  (partStatusState, schemas, partIds) => {
    let loading = false;
    _.forEach(partIds, (partId) => {
      _.forEach(schemas, (s) => {
        const status = partStatusState[partId]?.[s];
        if (status && LoadingStatusTypes.includes(status)) {
          loading = true;
        }
      });
    });
    return loading;
  }
)((__, schemas, partIds) => `${_.join(partIds, '_')}_${_.join(schemas, '_')}`);

export const selectLoadingStateBySchemaForList = createCachedSelector(
  getPartStatusSelector,
  (__: RootState, schemas: PartSchemas[]) => schemas,
  (__: RootState, ___, partIds: { [key: PartId]: PartId[] }) => partIds,
  (__: RootState, ____, listName: string) => listName,
  (partStatusState, schemas, partIds, __) =>
    _.reduce(
      Object.keys(partIds),
      (acc, key) => {
        let loading = false;
        _.forEach(partIds[key], (partId) => {
          _.forEach(schemas, (s) => {
            const status = partStatusState[partId]?.[s];
            if (status && LoadingStatusTypes.includes(status)) {
              loading = true;
            }
          });
        });
        acc[key] = loading;
        return acc;
      },
      {} as { [key: PartId]: boolean }
    )
)(
  (__, schemas, ___, listName) =>
    `LoadingStateForList-${listName}-${_.join(schemas, '_')}`
);

export const selectPartStatusRequestBySchemaForList = createCachedSelector(
  getPartStatusSelector,
  (__: RootState, schema: PartSchemas) => schema,
  (__: RootState, ___, partIds: PartId[]) => partIds,
  (partStatusState, schema, partIds) =>
    _.reduce(
      partIds,
      (acc) => {
        _.forEach(partIds, (partId) => {
          const status = partStatusState[partId]?.[schema];
          if (status) {
            acc[partId] = status;
          }
        });
        return acc;
      },
      {} as { [key: PartId]: partStatusRequestTypes }
    )
)(
  (__, schema, partIds) => `PartStatusRequest-${schema}-${_.join(partIds, '_')}`
);
